import * as angular from 'angular';
import * as _ from 'lodash';

import { ChartDirectiveConfiguration } from '../../../wtFramework';
import { Group, IValueViewModel, StateDto, ComponentDefinition } from '../../../models';
import { IRootScope } from '../../../IRootScope';
import { IGroupService, ILoggerService, IStateService, IStoreService } from '../../../services';
import { allRulesUpdated, oneRulesUpdated, wtDroppableDrop, groupRulesAddState } from '../../../common';
import { StoreElement, Store } from '../../../stateManagement';

interface IGroupRulesState {
    selectedGroup: Group;
    selectedRule: any;
    deleteFromGroup(stateGuid: string);
    selectedIdentifier: IValueViewModel;
}

class GroupRulesStateController implements IGroupRulesState {
    group: Group;
    groupGuid: string;
    groups: Group[];
    isEditMode: boolean;
    isLoaded: boolean;
    isLoading: boolean;
    loadingMessage: string;
    selectedGroup: Group;
    selectedIdentifier: IValueViewModel;
    selectedRule: any;
    states: StateDto[] = [];
    trend: ChartDirectiveConfiguration;
    viewModels: { [guid: string]: IValueViewModel };

    private _garbages: Function[] = [];

    // overwritten in widget to be called by modal
    deleteFromGroup(stateGuid: string) {
    }

    static $inject = [
        '$scope',
        '$rootScope',
        'storeService',
        'groupService',
        'stateService',
        'loggerService',
    ];

    constructor(private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _storeService: IStoreService,
        private _groupService: IGroupService,
        private _stateService: IStateService,
        private _loggerService: ILoggerService, ) {
    }

    $onInit() {
        this.isEditMode = this._$rootScope.isEditMode;
        this.groupGuid = this.group.guid;

        this._$scope.$watch(() => this.selectedIdentifier, (newValue, oldValue) => {
            if (!newValue && oldValue) {
                this.selectedIdentifier = oldValue;
            }
        });

        this.isLoading = true;
        this._garbages.push(
            this._storeService.register(
                store => this.refreshStore(store),
                [StoreElement.Groups, StoreElement.ValueViewModels]
            )
        );

        this.selectedGroup = this.group;

        this._stateService
            .getUsedByGroup(this.groupGuid)
            .then(data => this.states = data);

        this._$scope.$on(wtDroppableDrop, (event, data) => {
            this._$rootScope.$broadcast(groupRulesAddState, data);
        });
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    $onChanges() {
        this.selectedGroup = this.group;
        this.reselectFirst();
    }

    reselectFirst(): void {
        const hasIdentifierInGroup = this.selectedGroup && this.selectedGroup.identifierCollection.length > 0;
        const hasSelected = !!this.selectedIdentifier;

        if (hasIdentifierInGroup && !hasSelected && this.viewModels) {
            const lowest = _.minBy(this.selectedGroup.identifierCollection, o => o.sortingOrder);
            if (lowest && this.viewModels[lowest.guid]) {
                this.selectedIdentifier = this.viewModels[lowest.guid];
            }
        }
    }

    fitAll() {
        this.loadingMessage = 'Fitting rules...';
        this.isLoading = true;
        this._groupService.autofit(this.selectedGroup)
            .then(() => {
                this._loggerService.logSuccess('Rules successfully fitted.');
                this._$rootScope.$broadcast(allRulesUpdated);
            })
            .catch(() => this._loggerService.logError('Failed to fit rules.'))
            .finally(() => this.isLoading = false);
    }

    fitOne() {
        this._groupService
            .fitone(this.groupGuid, this.selectedIdentifier.guid)
            .then(() => {
                this._loggerService.logSuccess('Rules successfully fitted.');
                this._$rootScope.$broadcast(oneRulesUpdated);
            })
            .catch(() => this._loggerService.logError('Failed to fit rules.'));
    }

    private refreshStore(store: Store) {
        this.groups = _.values<Group>(store.groups.dictionary);
        this.viewModels = store.valueViewModels.dictionary;
        this.reselectFirst();
        this.isLoading = false;
        this.isLoaded = true;
    }
}

export const wtGroupRulesState: ComponentDefinition = {
    componentId: 'wtGroupRulesState',
    controller: GroupRulesStateController,
    template: require('./groupRulesState.component.html'),
    controllerAs: 'vm',
    bindings: {
        group: '<'
    }
};